<template>
  <div>
    <h3 ref="ca">
      Trigonometric Ratios
    </h3>
    <p>
      Trigonometry is the study of the relation between the sides and angles of a right-angled triangle.
      A right-angled triangle is a triangle with one of the angles as right angle (
      \(90^o\)). The triangle ABC shown below is a right-angled triangle with the angle at vertex 'A' being the right angle.
      <v-layout justify-center>
        <v-img src="/assets/right-triangle.png"
               max-height="450px"
               max-width="450px"
               contain
        />
      </v-layout>
      A trigonometric ratio simply refers to the ratio between the lengths of the sides of a right-angled triangle.
      There are three basic trigonometric ratios: sine (denoted as \(\sin\)), cosine (denoted as \(\cos\)) and tangent (denoted as \(\tan\)).
      These ratios are defined in relation to one of the acute angles (i.e. the angle that is not \(90^o\)) of the triangle.
      For example, the ratios corresponding to angle &alpha; of the triangle ABC shown above are defined as follows:
      <ul class="a">
        <li>
          $$\sin\theta = \frac{\text{Opposite Side}}{\text{Hypotenuse}}$$
          Here the opposite side refers to the side across from the angle &theta; (= AC).
          Hypotenuse refers to the side opposite to the right-angle (= BC).
        </li>
        <li>
          $$\cos\theta = \frac{\text{Adjacent Side}}{\text{Hypotenuse}}$$
          Here the adjacent side refers to the side next to the angle &theta; (= AB).
        </li>
        <li> $$\tan\theta = \frac{\text{Opposite Side}}{\text{Adjacent Side}}$$ </li>
      </ul>
    </p>
    <br>

    <h3 ref="sa">
      Calculating Trigonometric Ratios
    </h3>
    <p>
      Trigonometric ratios can be represented by means of a unit circle, i.e. a circle that is centered at the origin and has unit radius.
      Consider a point 'A' that is located on the circumference of this unit circle. Let's say the coordinates of the point A are \((x, y)\).
      Now, construct a right-angled triangle OAB such that
      <ul class="a">
        <li>
          Side OA is the hypotenuse of this triangle, and represents the radius of the circle, i.e.,  OA = 1 unit.
        </li> <br>
        <li> Side AB is the height of the triangle, and represents the projection of point A on x-axis, i.e.,  \(AB = x\). </li> <br>
        <li> Side OB is the base of the triangle, and represents the projection of point A on y-axis, i.e., \(OB = y\). </li> <br>
      </ul>
      Now, using the definitions of trigonometric ratios, we can relate the coordinates of point A to the various trigonometric ratios as follows:
      $$\sin \theta = \frac{\text{Height (AB)}}{\text{Hypotenuse (OA)}} = \frac{x}{1}  = x$$
      $$\cos \theta = \frac{\text{Base (OB)}}{\text{Hypotenuse (OA)}} = \frac{y}{1} = y$$
      $$\tan \theta = \frac{\text{Height (AB)}}{\text{Base (OB)}} = \frac{x}{y}$$
    </p>
    <h3 ref="playgraph">
      MagicGraph &vert; Unit Circle
    </h3>
    <v-responsive>
      <v-layout justify-center>
        <div id="jxgbox1a" class="edliy-box-about" />
      </v-layout>
    </v-responsive>
    <!--    <v-layout justify-center>
      <v-img src="/assets/supplementary_angles.png"
             max-height="450px"
             max-width="450px"
             contain
      />
    </v-layout> -->
    <br>
    <h3 ref="playgraph">
      MagicGraph &vert; Applications of Trigonometry
    </h3>
    <p>
      This MagicGraph offers a collection of visually interactive modules on application of trigonometry to find distance, heights and angles.
    </p>
    <!--    <h5> To Get Started: </h5>
    <p>
    You are given a triangle formed by points O, A and B.
    The vertices O, A, and B of the triangle can be dragged and moved around to change
    the type (i.e., acute, obtuse or right) and size of the triangle.
    You will be able to see that the three interior angles &alpha;, &beta; and &gamma; change as the vertices are dragged around.
    </p>
<h5> To Explore:</h5>
    <p>
 Drag points O, A and B of the triangle to change the type (i.e., acute, obtuse or right) and size of the triangle OAB.
 Then, tap on the two scissors to cut out the angles &alpha; and &beta;.
 No matter what type or size of the triangle you choose, the three interior
 angles &alpha;, &beta; and &gamma; always form a half circle when stacked adjacent to each other.
 </p> -->
    <v-responsive>
      <v-layout justify-center>
        <div id="jxgbox1b" class="edliy-box-about" />
      </v-layout>
    </v-responsive>
  </div>
</template>
<script>
import Boxes from './Boxes.js'
export default {
  name: 'Angles',
  created: function () {
      this.$store.commit('navigation/resetState');
    // Store mutations
    let title = 'Trigonometry';
    this.$store.commit('navigation/changeTitle', title);
    this.$store.commit('navigation/changeMenu', title);
    let newTopics = [
      {title: 'complementary Angle', img:'/assets/number-1.svg', action: () => this.goto('ca')},
      {title: 'Supplementary Angle', img:'/assets/number-2.svg', action: () => this.goto('sa')},
      {title: 'Special Remarks', img:'/assets/number-3.svg', action: () => this.goto('ra')},
      {title: 'MagicGraph',img:'/assets/touch.svg', action: () => this.goto('playgraph')},
    ];
    this.$store.commit('navigation/replaceTopics', newTopics);
    let newshowhome = false;
    this.$store.commit('navigation/toggleshowhome', newshowhome);
    let newMath =true;
    this.$store.commit('navigation/replaceMath', newMath);
    let newLeftArrow =true;
    this.$store.commit('navigation/replaceLeftArrow', newLeftArrow);
    let newModule=true;
    this.$store.commit('navigation/replaceModule', newModule);
  },
  mounted () {
    MathJax.Hub.Queue(["Typeset", MathJax.Hub]);
    Boxes.box1();
    Boxes.box2();
  },
  metaInfo() {
  return{ title: 'Trigonometry',
          titleTemplate: '%s - Learn interactively',
          meta: [ {name: 'viewport', content: 'width=device-width, initial-scale=1'},
                  {name: 'description', content: 'Learn interactively about trigonometric functions.'}
                ]
        }
   },
}
</script>
<style lang="scss">
@import 'src/styles/edliy-box.scss';
@import 'src/styles/edliy-box.scss';
@import 'src/styles/subtopic-menu.scss';
@import 'src/styles/edliy-box-about.scss';
</style>
